// wxstations layer family — METAR/TAF station observations + forecasts.
//
// One GeoJSON source (`wxstations`) drives multiple visualizations:
//   - flight-category dot (circle background + colored core + label)
//   - surface-wind barb (symbol layer)
//   - ceiling/visibility (circle + label, with sky-cover wedge baked in)
//   - temperature (circle + label)
//   - dew-point spread (circle + label)
//
// Each visualization filters on the time-scrubber via `validMs`, so a
// single render covers "now" plus the bracketing forecast frame.
//
// Extracted from addOverlays.ts as HI-1 batch 6a (audit 2026-05-22):
// these layers were the most-flagged silent-dropout victims because
// they involve both a one-time filter re-apply (via
// applyStationLayerFilter) AND a basemap-rebuild restore. Owning that
// restore here, via registerStyleRebuild(), closes the silent-dropout
// bug class for this family permanently \u2014 the registry runs the
// filter re-apply automatically after every basemap switch.

import type { Map as MlMap } from 'maplibre-gl';
import { useUiStore } from '../../stores/uiStore';
import { useLayerVisibilityStore } from '../../stores/layerVisibilityStore';
import { EMPTY_FC, applyFlightCatFilter } from '../MapView';
import { ensureSkyCoverIcons, SKY_COVER_ICON_EXPRESSION } from '../skyCoverIcons';
import { ensureWindBarbIcons, WIND_BARB_ICON_EXPRESSION } from '../windBarbs';
import { applyStationLayerFilter } from '../mapFilters';
import { registerLayerFamily } from '../layerVisibilityRegistry';
import { isOverlayLoaded, markOverlayLoaded, refreshOverlay } from '../overlayRuntime';
import { OVERLAY_BY_ID } from '../overlaySpecs';

/** All layer ids owned by this family. Used by the registered style-rebuild
 *  restore to re-apply the time filter after a basemap setStyle() rebuild. */
const WXSTATIONS_FILTERED_LAYER_IDS = [
  'surfacewinds-symbol',
  'ceilvis-circle', 'ceilvis-label',
  'temperature-circle', 'temperature-label',
  'dewspread-circle', 'dewspread-label',
] as const;

/**
 * Mount the wxstations source + all derived layers into the map.
 *
 * Idempotent at the source level: if `wxstations` already exists, the
 * function is a no-op. (Same shape as the pre-extraction code that lived
 * inline in addOverlays.ts.)
 *
 * Side effect: registers a style-rebuild restore the first time it's
 * called per page load, so a future basemap switch re-applies the time
 * filter and re-mounts the wxstations layers automatically.
 */
export function addWxStationsLayers(map: MlMap): void {
  // ---- Flight Category dot map ----------------------------------------
  // METAR + TAF station observations + forecasts as colored circles.
  // One source feeds multiple future dot-map layers (Wind / Vis /
  // Ceiling / etc. — same data, different color-by). The current
  // time-scrubber position picks which forecast frame each station
  // shows via a layer filter on the validMs property.
  if (!map.getSource('wxstations')) {
    map.addSource('wxstations', { type: 'geojson', data: EMPTY_FC });

    // Outline ring for legibility against busy chart backgrounds. The
    // colored core sits inside this ring.
    map.addLayer({
      id: 'flightcat-circle-bg',
      type: 'circle',
      source: 'wxstations',
      layout: { visibility: 'none' },
      paint: {
        'circle-color': '#0b0d0f',
        'circle-radius': [
          'interpolate', ['linear'], ['zoom'],
          4, 5,
          7, 8,
          11, 12,
        ],
        'circle-stroke-color': '#0b0d0f',
        'circle-stroke-width': 0.5,
      },
    });
    map.addLayer({
      id: 'flightcat-circle',
      type: 'circle',
      source: 'wxstations',
      layout: { visibility: 'none' },
      paint: {
        'circle-color': [
          'match', ['coalesce', ['get', 'fltCat'], 'UNKN'],
          'VFR',  '#22c55e',  // green
          'MVFR', '#3b82f6',  // blue
          'IFR',  '#dc2626',  // red
          'LIFR', '#a855f7',  // magenta/purple
          /* default UNKN */ '#6b7280',
        ],
        'circle-radius': [
          'interpolate', ['linear'], ['zoom'],
          4, 3.5,
          7, 6,
          11, 9,
        ],
        'circle-stroke-color': '#ffffff',
        'circle-stroke-width': 1.2,
        'circle-opacity': 0.95,
      },
    });
    // ICAO label — only at higher zoom so the dots stay clean far out.
    map.addLayer({
      id: 'flightcat-label',
      type: 'symbol',
      source: 'wxstations',
      layout: {
        visibility: 'none',
        'text-field': ['coalesce', ['get', 'icaoId'], ''],
        'text-size': 9,
        'text-anchor': 'top',
        'text-offset': [0, 0.7],
        'text-font': ['Noto Sans Regular'],
        'text-allow-overlap': false,
        'text-ignore-placement': false,
      },
      paint: {
        'text-color': '#e5e7eb',
        'text-halo-color': '#0b0d0f',
        'text-halo-width': 1.4,
      },
      minzoom: 7,
    });

    // ---- Surface Winds (wind barbs) -----------------------------------
    // One symbol layer reading the right barb icon per windKt bucket
    // and rotating by windDirDeg. Same source as flight-cat.
    void ensureWindBarbIcons(map);
    map.addLayer({
      id: 'surfacewinds-symbol',
      type: 'symbol',
      source: 'wxstations',
      layout: {
        visibility: 'none',
        'icon-image': WIND_BARB_ICON_EXPRESSION,
        // Rotate the barb to point INTO the wind (tail toward the
        // direction wind is FROM). MapLibre's icon-rotate is degrees
        // clockwise from north — same convention as windDirDeg.
        'icon-rotate': ['coalesce', ['to-number', ['get', 'windDirDeg']], 0],
        'icon-rotation-alignment': 'map',
        'icon-size': [
          'interpolate', ['linear'], ['zoom'],
          4, 0.45,
          7, 0.7,
          11, 1.0,
        ],
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
        'icon-anchor': 'bottom', // origin ("bottom" of icon = station dot)
      },
    });

    // ---- Ceiling + Visibility (combined numerical readout) -------------
    // ONE marker per station with a two-line text label showing the
    // actual numbers, not a color-coded category. Boss preferred this
    // over the prior two-layer color-coding which was too easy to
    // confuse with flight-cat dots.
    //
    // Marker: white circle with dark navy stroke — a neutral color
    // not used by any other layer (avoids collision with flight-cat
    // green/red/blue/purple, GFA cyan/orange/yellow, fires red, etc.).
    //
    // Label format (two lines, right of the marker):
    //   line 1 (white):       ceiling in hundreds of feet (e.g. "·18" =
    //                         1800 ft AGL, "—" = no ceiling reported)
    //   line 2 (cream/yellow): visibility in statute miles
    //                         (e.g. "10", "1¼", "M¼" for < 1/4)
    // Ceiling/visibility marker uses the same WMO-style sky-coverage symbol
    // set (CLR / FEW / SCT / BKN / OVC / OVX) as the old standalone 'skycover'
    // layer — the icons are white circles with a dark slice for coverage, so
    // they double as the neutral marker AND the sky-cover readout.
    // The previous separate 'skycover-symbol' layer was redundant. Boss merge
    // request 2026-05-21 19:13 PDT.
    void ensureSkyCoverIcons(map);
    map.addLayer({
      id: 'ceilvis-circle',
      type: 'symbol',
      source: 'wxstations',
      layout: {
        visibility: 'none',
        'icon-image': SKY_COVER_ICON_EXPRESSION,
        'icon-size': [
          'interpolate', ['linear'], ['zoom'],
          4, 0.45, 7, 0.65, 11, 0.85,
        ],
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
      },
    });
    map.addLayer({
      id: 'ceilvis-label',
      type: 'symbol',
      source: 'wxstations',
      minzoom: 5,
      layout: {
        visibility: 'none',
        // Two-line text: ceiling on line 1, visibility on line 2.
        // The proxy provides ceilingFt (feet) and visibilitySm.
        // Render ceiling as hundreds of feet ("18" = 1800 ft) so
        // common values fit in two characters.
        'text-field': [
          'concat',
          // Line 1: ceiling in 100s of feet (e.g. 1800 ft → "18").
          // 'has' returns true even for null values, so we also need
          // to check that to-number doesn't yield 0 from a null.
          ['case',
            ['all',
              ['has', 'ceilingFt'],
              ['!=', ['to-number', ['coalesce', ['get', 'ceilingFt'], 0]], 0],
            ],
            ['to-string', ['round', ['/', ['to-number', ['get', 'ceilingFt']], 100]]],
            '—',
          ],
          '\n',
          // Line 2: visibility. "M" prefix means "less than" per FAA
          // METAR convention; we apply it for values < 1/4.
          ['case',
            ['all',
              ['has', 'visibilitySm'],
              ['!=', ['to-number', ['coalesce', ['get', 'visibilitySm'], -1]], -1],
            ],
            ['case',
              ['<', ['to-number', ['get', 'visibilitySm']], 0.25], 'M¼',
              ['<', ['to-number', ['get', 'visibilitySm']], 0.5],  '¼',
              ['<', ['to-number', ['get', 'visibilitySm']], 0.75], '½',
              ['<', ['to-number', ['get', 'visibilitySm']], 1],    '¾',
              ['to-string', ['round', ['to-number', ['get', 'visibilitySm']]]],
            ],
            '?',
          ],
        ],
        'text-size': 12,
        // Right of the dot, top-aligned so the two lines stack neatly.
        'text-anchor': 'left',
        'text-offset': [0.85, 0],
        'text-justify': 'left',
        'text-font': ['Noto Sans Regular'],
        'text-allow-overlap': false,
        'text-ignore-placement': false,
      },
      paint: {
        // White text with dark halo — readable on Sectional + satellite.
        'text-color': '#f8fafc',
        'text-halo-color': '#0f172a',
        'text-halo-width': 1.6,
      },
    });

    // ---- Temperature readout -------------------------------------------
    // Same neutral marker as ceilvis. Single-line text label of OAT in
    // °C. Stations without temp data render no label.
    map.addLayer({
      id: 'temperature-circle',
      type: 'circle',
      source: 'wxstations',
      layout: { visibility: 'none' },
      paint: {
        'circle-color': '#f8fafc',
        'circle-radius': [
          'interpolate', ['linear'], ['zoom'],
          4, 3, 7, 5, 11, 7,
        ],
        'circle-stroke-color': '#0f172a',
        'circle-stroke-width': 1.5,
        'circle-opacity': 0.95,
      },
    });
    map.addLayer({
      id: 'temperature-label',
      type: 'symbol',
      source: 'wxstations',
      minzoom: 5,
      layout: {
        visibility: 'none',
        'text-field': [
          'case',
          ['all',
            ['has', 'tempC'],
            // Same null-vs-0 guard as ceilvis. Use a sentinel that
            // can't realistically appear in real OAT data.
            ['!=', ['to-number', ['coalesce', ['get', 'tempC'], -999]], -999],
          ],
          ['concat', ['to-string', ['round', ['to-number', ['get', 'tempC']]]], '°'],
          '',
        ],
        'text-size': 11,
        'text-anchor': 'left',
        'text-offset': [0.65, 0],
        'text-font': ['Noto Sans Regular'],
      },
      paint: {
        'text-color': '#f8fafc',
        'text-halo-color': '#0f172a',
        'text-halo-width': 1.6,
      },
    });

    // ---- Dewpoint Spread (T − Td) -------------------------------------
    // Color the marker by spread: red < 2°C, yellow 2–5, green ≥ 5.
    // Numerical label shows the spread to the right.
    map.addLayer({
      id: 'dewspread-circle',
      type: 'circle',
      source: 'wxstations',
      layout: { visibility: 'none' },
      paint: {
        'circle-color': [
          'case',
          ['any',
            ['!', ['has', 'tempC']], ['!', ['has', 'dewpC']],
            ['==', ['to-number', ['coalesce', ['get', 'tempC'], -999]], -999],
            ['==', ['to-number', ['coalesce', ['get', 'dewpC'], -999]], -999],
          ], '#6b7280',
          ['<', ['-', ['to-number', ['get', 'tempC']], ['to-number', ['get', 'dewpC']]], 2], '#dc2626',
          ['<', ['-', ['to-number', ['get', 'tempC']], ['to-number', ['get', 'dewpC']]], 5], '#facc15',
          /* default */ '#22c55e',
        ],
        'circle-radius': [
          'interpolate', ['linear'], ['zoom'],
          4, 3.5, 7, 6, 11, 9,
        ],
        'circle-stroke-color': '#ffffff',
        'circle-stroke-width': 1.2,
        'circle-opacity': 0.95,
      },
    });
    map.addLayer({
      id: 'dewspread-label',
      type: 'symbol',
      source: 'wxstations',
      minzoom: 6,
      layout: {
        visibility: 'none',
        'text-field': [
          'case',
          ['all',
            ['has', 'tempC'],
            ['has', 'dewpC'],
            ['!=', ['to-number', ['coalesce', ['get', 'tempC'], -999]], -999],
            ['!=', ['to-number', ['coalesce', ['get', 'dewpC'], -999]], -999],
          ],
          ['concat',
            ['to-string', ['round', ['-', ['to-number', ['get', 'tempC']], ['to-number', ['get', 'dewpC']]]]],
            '°'],
          '',
        ],
        'text-size': 10,
        'text-anchor': 'left',
        'text-offset': [0.65, 0],
        'text-font': ['Noto Sans Regular'],
      },
      paint: {
        'text-color': '#f8fafc',
        'text-halo-color': '#0f172a',
        'text-halo-width': 1.6,
      },
    });

    // (Standalone 'skycover-symbol' layer removed 2026-05-21 — the sky-cover
    // wedge is now drawn directly inside 'ceilvis-circle' which uses the
    // same SKY_COVER_ICON_EXPRESSION.)

    // Apply initial time filter to ALL station-derived layers so the
    // slider state from before mount is honored.
    const initialOffsetH = useUiStore.getState().wxHazardOffsetH;
    applyFlightCatFilter(map, initialOffsetH);
    applyStationLayerFilter(map, [
      'surfacewinds-symbol',
      'ceilvis-circle', 'ceilvis-label',
      'temperature-circle', 'temperature-label',
      'dewspread-circle', 'dewspread-label',
    ], initialOffsetH);
  }
}

// Register the wxstations layer family using the unified spec shape.
// visibilitySelector: family is "on" when any station-derived layer is on.
// applyState: handles both visibility (per-sub-layer) and time-scrubber filters.
registerLayerFamily({
  id: 'wxstations',
  label: 'Weather stations (METAR/TAF)',
  sourceId: 'wxstations',
  layerIds: [
    'flightcat-circle-bg', 'flightcat-circle', 'flightcat-label',
    ...WXSTATIONS_FILTERED_LAYER_IDS,
  ],
  mount: (map) => addWxStationsLayers(map),
  // visibilitySelector: returns true when any station-derived layer is on.
  // Used by runVisibilityHandlers for OFF→ON transition detection
  // (onToggleOn triggers the initial METAR+TAF fetch).
  visibilitySelector: (l) => l.flightcat || l.surfacewinds || l.ceilvis || l.temperature || l.dewspread,
  applyState: (map) => {
    const layers = useLayerVisibilityStore.getState().layers;
    const offsetH = useUiStore.getState().wxHazardOffsetH;

    // Per-sub-layer visibility. Each sub-layer has an independent toggle.
    const setVis = (ids: readonly string[], on: boolean) => {
      const v = on ? 'visible' : 'none';
      for (const id of ids) {
        if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', v);
      }
    };
    setVis(['flightcat-circle-bg', 'flightcat-circle', 'flightcat-label'], layers.flightcat);
    setVis(['surfacewinds-symbol'], layers.surfacewinds);
    setVis(['ceilvis-circle', 'ceilvis-label'], layers.ceilvis);
    setVis(['temperature-circle', 'temperature-label'], layers.temperature);
    setVis(['dewspread-circle', 'dewspread-label'], layers.dewspread);

    // Re-apply time-scrubber filters (always apply so data is correct
    // when any sub-layer toggles on).
    applyFlightCatFilter(map, offsetH);
    applyStationLayerFilter(map, [...WXSTATIONS_FILTERED_LAYER_IDS], offsetH);
  },
  // onToggleOn: fire the shared METAR+TAF fetch once when any station
  // layer first turns on. Uses the existing overlayRuntime guard.
  onToggleOn: (map) => {
    if (isOverlayLoaded('flightcat')) return;
    markOverlayLoaded('flightcat');
    const spec = OVERLAY_BY_ID.get('flightcat');
    if (spec) return refreshOverlay(map, spec);
  },
  // bboxRefresh: refresh station data on viewport change when loaded.
  bboxRefresh: (map) => {
    if (!isOverlayLoaded('flightcat')) return;
    const spec = OVERLAY_BY_ID.get('flightcat');
    if (!spec) return;
    return refreshOverlay(map, spec);
  },
});
